import { useEffect, useMemo } from 'react';
import { withStyles, Card, Icon, MaterialSymbol } from 'vopay';
import { Title, CircleButton, Chip } from 'UI';
import styles from './CustomerOverview.module.scss';
import { useDialog } from '@/providers/Dialog.provider';
import useLabels from '@/hooks/useApi/useLabels';
import { useToast } from '@/providers/Toast.provider';
import { useDataFetchIndicator } from '@/hooks';
import AddLabelDialog from './AddLabelDialog/AddLabelDialog';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type OverviewProps = {
  accountID: string;
  active: boolean;
  accountName: string;
  lang: string;
  isBusiness: boolean;
};

function CustomerOverview({ accountID, active, accountName, lang, isBusiness }: OverviewProps) {
  const [, setPending] = useDataFetchIndicator();
  const { labelsLoaded, fetchingLabels, labels: customerLabels, fetchLabels, deleteLabel } = useLabels(accountID);
  const dialog = useDialog();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.Overview' });

  useEffect(() => {
    setPending(fetchingLabels, '', 'fullScreen');
  }, [fetchingLabels]);

  async function handleAddLabelClick() {
    if (!customerLabels) return;

    const addedLabel = await dialog.open<string>(({ onConfirm, onCancel }) => (
      <AddLabelDialog customerLabels={customerLabels} onClose={onCancel} onAdd={onConfirm} accountID={accountID} />
    ));

    if (addedLabel) {
      await fetchLabels();

      toast.success(
        t('labelAdded', {
          defaultValue: `${addedLabel} Label added to ${accountName}`,
          addedLabel,
          accountName,
        }),
      );
    }
  }

  async function handleDeleteLabelClick(removedLabel: string) {
    try {
      await deleteLabel(removedLabel);

      toast.neutral(
        t('labelRemoved', {
          defaultValue: `${removedLabel} Label Removed from ${accountName}`,
          removedLabel,
          accountName,
        }),
      );
    } catch (err) {
      console.error(err);
      toast.neutral(
        t('labelRemoveFail', {
          defaultValue: `Failed to remove ${removedLabel} Label from ${accountName}`,
          removedLabel,
          accountName,
        }),
      );
    }
  }

  const avatar = useMemo(() => {
    if (isBusiness) {
      return active ? 'businessAvatar' : 'businessAvatarInactive';
    }

    return active ? 'personAvatar' : 'personAvatarInactive';
  }, [isBusiness, active]);

  return (
    <Card className={cn('accountSummaryCard')}>
      <div className={cn('accountSummary')}>
        <Icon icon={avatar} size="xlg" width="64px" height="64px" color="none" />
        <div className={cn('accountNameAndType')}>
          <div className={cn('accountNameAndLang')}>
            <Title level="4" className={cn('accountName')}>
              {accountName}
            </Title>

            <span className={cn('langBadge')}>
              <MaterialSymbol icon="language" size="17px" />
              {lang}
            </span>
          </div>

          <div className={cn('accountType')}>
            {isBusiness ? t('business', { defaultValue: 'Business' }) : t('individual', { defaultValue: 'Individual' })}
          </div>
        </div>
        <div className={cn('activeBadge', active && 'active')}>
          <Icon icon={active ? 'checkGreen' : 'inactive'} color="none" size="sm" width="20px" height="20px" />
          {active ? t('active', { defaultValue: 'Active' }) : t('inactive', { defaultValue: 'Inactive' })}
        </div>
      </div>

      {labelsLoaded && (
        <div data-testid="label-container" className={cn('labelContainer')}>
          <div className={cn('labels', 'scrollbar')}>
            {customerLabels.length > 0
              ? customerLabels.map((label) => (
                  <Chip
                    disabled={!active}
                    key={label}
                    label={label}
                    {...(active && { iconRight: 'close' })}
                    onClick={() => handleDeleteLabelClick(label)}
                  />
                ))
              : 'No Labels'}
          </div>

          {active && <CircleButton icon="add" size="large" onClick={handleAddLabelClick} circleFill={false} />}
        </div>
      )}
    </Card>
  );
}

export default CustomerOverview;
